import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from "react-intl";

import {styled, useTheme} from "@mui/material/styles";

import {
    black500,
    blue25,
    green500,
    green700,
    greyishBlue100,
    greyishBlue400,
    greyishBlue500,
    greyishBlue700,
    orange500,
    orange700,
    red500,
    red700,
} from "assets/jss/main";

import Badge from "components/Badge";
import RightDrawerDesktop from "components/WMS/Desktop/RightDrawerDesktop";

const pointsShape = PropTypes.shape({
    points: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
});

ScoringInfoDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    // full scoring breakdown (ES field `scoring` on the product document,
    // ScoringBreakdown::toArray() on the API side); each bloc also carries its
    // per-criterion detail, e.g. commercialPerformance.onlineSales.{points,max}
    scoring: PropTypes.shape({
        score: PropTypes.number.isRequired,
        potentialScore: PropTypes.number.isRequired,
        maxScore: PropTypes.number.isRequired,
        commercialPerformance: pointsShape.isRequired,
        attractiveness: pointsShape.isRequired,
        availability: pointsShape.isRequired,
        retailerPower: pointsShape.isRequired,
    }).isRequired,
    // createdAt of the selling-properties snapshot the score was computed from
    // (ES field `scoringDate`, RFC3339); the "computed on" line hides without it
    scoringDate: PropTypes.string,
};

const LEVELS = {
    good: {badgeColor: 'online', badgeLabelColor: green700, criterionColor: green500},
    warning: {badgeColor: 'warning', badgeLabelColor: orange700, criterionColor: orange500},
    bad: {badgeColor: 'error', badgeLabelColor: red700, criterionColor: red500},
};

// color bands for a points/max ratio (blocs, criteria and potential score); the
// 55 "to enrich" threshold only applies to the global score pill
const levelForPoints = ({points, max}) => {
    const ratio = points / max;

    if (ratio < 0.40) {
        return 'bad';
    }

    if (ratio < 0.70) {
        return 'warning';
    }

    return 'good';
};

// one entry per bloc of the breakdown, in display order; keys are the field
// names of the ES `scoring` object
const SECTIONS = [
    {
        key: 'commercialPerformance',
        titleId: "retailer.product.scoring.drawer.section.performance.title",
        criteria: [
            {
                key: 'onlineSales',
                labelId: "retailer.product.scoring.drawer.section.performance.sales.label",
                descriptionId: "retailer.product.scoring.drawer.section.performance.sales.description",
                usesValue: true,
            },
            {
                key: 'returnRate',
                labelId: "retailer.product.scoring.drawer.section.performance.returnRate.label",
                descriptionId: "retailer.product.scoring.drawer.section.performance.returnRate.description",
                usesValue: true,
            },
        ],
    },
    {
        key: 'attractiveness',
        titleId: "retailer.product.scoring.drawer.section.attractiveness.title",
        criteria: [
            {
                key: 'ctr',
                labelId: "retailer.product.scoring.drawer.section.attractiveness.ctr.label",
                descriptionId: "retailer.product.scoring.drawer.section.attractiveness.ctr.description",
                usesValue: true,
            },
            {
                key: 'imageCount',
                labelId: "retailer.product.scoring.drawer.section.attractiveness.photos.label",
                descriptionId: "retailer.product.scoring.drawer.section.attractiveness.photos.description",
                usesValue: true,
            },
        ],
    },
    {
        key: 'availability',
        titleId: "retailer.product.scoring.drawer.section.availability.title",
        criteria: [
            {
                key: 'stock',
                labelId: "retailer.product.scoring.drawer.section.availability.stock.label",
                descriptionId: "retailer.product.scoring.drawer.section.availability.stock.description",
                usesValue: true,
            },
        ],
    },
    {
        key: 'retailerPower',
        titleId: "retailer.product.scoring.drawer.section.retailerPower.title",
        criteria: [
            {
                key: 'sellingPower',
                labelId: "retailer.product.scoring.drawer.section.retailerPower.salesPower.label",
                descriptionId: "retailer.product.scoring.drawer.section.retailerPower.salesPower.description",
            },
            {
                key: 'shares',
                labelId: "retailer.product.scoring.drawer.section.retailerPower.trafficShare.label",
                descriptionId: "retailer.product.scoring.drawer.section.retailerPower.trafficShare.description",
            },
        ],
    },
];

const TitleContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
});

const Title = styled('span')({
    fontSize: '24px',
    fontWeight: 700,
    color: black500
});

const Subtitle = styled('span')({
    fontSize: '16px',
    fontWeight: 400,
    color: greyishBlue500
});

const SectionsContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
});

const Section = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
});

const SectionHeader = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
});

const SectionTitle = styled('span')({
    fontSize: '16px',
    fontWeight: 600,
    color: black500
});

const CriteriaContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
});

const Criterion = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px'
});

const CriterionScore = styled('span', {shouldForwardProp: (prop) => 'level' !== prop})(({level}) => ({
    fontSize: '14px',
    fontWeight: 600,
    color: LEVELS[level].criterionColor
}));

const CriterionDescription = styled('span')({
    fontSize: '14px',
    fontWeight: 400,
    color: greyishBlue500
});

const PotentialContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    marginTop: '32px',
    padding: '8px 16px',
    backgroundColor: blue25,
    border: `1px solid ${greyishBlue100}`,
    borderRadius: '8px'
});

const PotentialHeader = styled('div')({
    display: 'flex',
    alignItems: 'center',
    gap: '4px'
});

const PotentialTitle = styled('span')({
    fontSize: '16px',
    fontWeight: 600,
    color: greyishBlue700
});

const ComputedAtText = styled('span')({
    marginTop: '16px',
    fontSize: '12px',
    fontWeight: 400,
    color: greyishBlue400
});

function ScoreBadge({level, label}) {
    return (
        <Badge
            color={LEVELS[level].badgeColor}
            labelColor={LEVELS[level].badgeLabelColor}
            indicator={'circle'}
            rounded
            border
            isBold={false}
            labelSize={14}
            label={label}
        />
    );
}

function ScoringInfoDrawer({open, onClose, scoring, scoringDate = null}) {
    const intl = useIntl();
    const theme = useTheme();

    return (
        <RightDrawerDesktop
            open={open}
            onClose={onClose}
            squareCorners
            blurredBackdrop
            // ProductTopBar sits above theme.zIndex.modal (so it stays over the
            // WYSIWYG toolbar) — this drawer needs to clear that same bar.
            zIndex={theme.zIndex.modal + 2}
            // per Figma, the close icon sits near the panel's top edge, independent
            // of the title block below it (unlike the shared default position)
            closeIconTop={16}
            closeIconRight={16}
            customWidth={"min(500px, 100vw)"}
            // with the content's own 24px top margin, 32px between the subtitle and the first bloc (Figma)
            customHeaderPadding={"0 72px 8px 24px"}
            customContentPadding={"24px"}
            withoutHeadBorder
            title={(
                <TitleContainer>
                    <Title>
                        <FormattedMessage id={"retailer.product.scoring.drawer.title"}/>
                    </Title>
                    <ScoreBadge
                        level={levelForPoints({points: scoring.score, max: 100})}
                        label={intl.formatMessage(
                            {id: "retailer.product.scoring.drawer.score"},
                            {score: scoring.score, max: scoring.maxScore}
                        )}
                    />
                </TitleContainer>
            )}
            subtitle={(
                <Subtitle>
                    <FormattedMessage id={"retailer.product.scoring.drawer.subtitle"}/>
                </Subtitle>
            )}
        >
            <SectionsContainer>
                {SECTIONS.map((section) => {
                    const sectionPoints = scoring[section.key];

                    return (
                        <Section key={section.key}>
                            <SectionHeader>
                                <SectionTitle>
                                    <FormattedMessage id={section.titleId}/>
                                </SectionTitle>
                                <ScoreBadge
                                    level={levelForPoints(sectionPoints)}
                                    label={`${sectionPoints.points}/${sectionPoints.max}`}
                                />
                            </SectionHeader>
                            <CriteriaContainer>
                                {section.criteria.map((criterion) => {
                                    const criterionPoints = sectionPoints[criterion.key];
                                    const rawValue = criterionPoints.value;
                                    const hasValue = typeof rawValue === 'number';

                                    return (
                                        <Criterion key={criterion.key}>
                                            <CriterionScore level={levelForPoints(criterionPoints)}>
                                                <FormattedMessage id={criterion.labelId}/>
                                                {` : ${criterionPoints.points}/${criterionPoints.max}`}
                                            </CriterionScore>
                                            <CriterionDescription>
                                                {!criterion.usesValue && (
                                                    <FormattedMessage id={criterion.descriptionId}/>
                                                )}
                                                {criterion.usesValue && hasValue && (
                                                    <FormattedMessage
                                                        id={criterion.descriptionId}
                                                        values={{value: intl.formatNumber(rawValue, {maximumFractionDigits: 1})}}
                                                    />
                                                )}
                                                {criterion.usesValue && !hasValue && (
                                                    <FormattedMessage id={"retailer.product.scoring.drawer.criterion.noData"}/>
                                                )}
                                            </CriterionDescription>
                                        </Criterion>
                                    );
                                })}
                            </CriteriaContainer>
                        </Section>
                    );
                })}
            </SectionsContainer>
            <PotentialContainer>
                <PotentialHeader>
                    <PotentialTitle>
                        <FormattedMessage id={"retailer.product.scoring.drawer.potential.title"}/>
                    </PotentialTitle>
                    <ScoreBadge
                        level={levelForPoints({points: scoring.potentialScore, max: scoring.maxScore})}
                        label={`${scoring.potentialScore}/${scoring.maxScore}`}
                    />
                </PotentialHeader>
                <CriterionDescription>
                    <FormattedMessage id={"retailer.product.scoring.drawer.potential.description"}/>
                </CriterionDescription>
            </PotentialContainer>
            {scoringDate && (
                <ComputedAtText>
                    <FormattedMessage
                        id={"retailer.product.scoring.drawer.computedAt"}
                        values={{date: intl.formatDate(scoringDate)}}
                    />
                </ComputedAtText>
            )}
        </RightDrawerDesktop>
    );
}

export default ScoringInfoDrawer;
